/**
 * @fileoverview List component for rendering vertical lists with icons and text content
 * Displays a list of items, each with an associated icon and text.
 *
 * @example
 * // Basic usage
 * const listItems = [
 *   { icon: 'file', text: 'Document 1' },
 *   { icon: 'folder', text: 'Folder 2' }
 * ];
 * <List list={listItems} />
 *
 * @example
 * // With custom styling
 * import css from './List.module.css';
 *
 * const items = [
 *   { icon: 'check', text: 'Completed task' },
 *   { icon: 'warning', text: 'Pending task' }
 * ];
 * <List list={items} />
 */


import { PropsWithChildren } from 'react'
import { Icon } from '../../Icon'
import { Text } from '../../typography/Typography'

import css from './List.module.css'

/**
 * @interface ListProps
 * @description Props for the List component
 * @property {Novoic.ListItem[]} list - The list of items to display
 */
interface ListProps extends PropsWithChildren {
    list: Novoic.ListItem[]
}

/**
 * @function List
 * @description Component for rendering vertical lists with icons and text content
 * @param {ListProps} props - The props for the component
 * @returns {JSX.Element} The List component
 */
export function List({ list }: ListProps) {
    return (
        <div className={css.container}>
            <ul className={css.list}>
                {list.map((item: Novoic.ListItem, index: number) => {
                    return (
                        <li key={`${item.icon}-${index}`}>
                            <Icon name={item.icon} />
                            <Text className={css.text}>{item.text}</Text>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}
